import React from 'react';
import { CustomerRecipe } from "../../../models/CustomerRecipe";
import { FormatHelper } from '../../../helpers/FormatHelper';
import { CollapsibleCard } from '../../../common-components/CollapsibleCard/CollapsibleCard';
import { DiagnosticBlock, DiagnosticContainerTable, DiagnosticDistanceType, HeaderCard, HeaderLeftContent, HeaderRightContent, RecipeFileBlock, RecipeIdentification, RecipeTypeDescription } from './styles';
import { RecipeType } from '../../../enums/RecipeType';
import { recipeResources } from '../../../resources/recipeResources';
import { strings } from '../../../resources/strings';
import { Table } from '../../../common-components/Table/Table';
import { TableRow } from '../../../common-components/TableRow/TableRow';
import { TableHead } from '../../../common-components/TableHead/TableHead';
import { TableData } from '../../../common-components/TableData/TableData';
import { Alert } from 'react-bootstrap';

interface ICustomerRecipeCardProps {
  recipe: CustomerRecipe;
}

const FixedTableHeadDiagnostic = (
  { children }: { children?: React.ReactNode }
) => <TableHead normalText backgroundColor='#f5f5f5' borderStyle='1px solid #ddd' width='90px' textAlign='center'>{children}</TableHead>;

const TableHeadDiagnostic = (
  { children }: { children?: React.ReactNode }
) => <TableHead normalText backgroundColor='#f5f5f5' borderStyle='1px solid #ddd' textAlign='center'>{children}</TableHead>;

const FixedTableDataDiagnostic = (
  { children }: { children?: React.ReactNode }
) => <TableData borderStyle='1px solid #ddd' textAlign='center'>{children}</TableData>;

const TableDataDiagnostic = (
  { children }: { children?: React.ReactNode }
) => <TableData borderStyle='1px solid #ddd' width='90px' textAlign='center'>{children}</TableData>;

const CustomerRecipeCard: React.FC<ICustomerRecipeCardProps> = ({
  recipe
}) => {

  let identification = '';
  if (recipe.name && recipe.description) {
    identification = `${recipe.description} (${recipe.name}) - ${FormatHelper.formatDateTime(recipe.createdAt)}`;
  } else if (recipe.name || recipe.description) {
    identification = `${recipe.description || ''} ${recipe.name || ''} - ${FormatHelper.formatDateTime(recipe.createdAt)}`;
  } else {
    identification = FormatHelper.formatDateTime(recipe.createdAt);
  }

  const header = (
    <HeaderCard>
      <HeaderLeftContent>
        <RecipeTypeDescription>
          {strings.Recipe} {recipe.recipeType === RecipeType.Bifocal ? recipeResources.BifocalRecipeTypeDescription : recipeResources.SingleVisionRecipeTypeDescription}
        </RecipeTypeDescription>
        <RecipeIdentification>
          {identification}
        </RecipeIdentification>
      </HeaderLeftContent>
      <HeaderRightContent>
      </HeaderRightContent>
    </HeaderCard>
  );

  const recipeFile = React.useMemo(() => {
    return (
      <RecipeFileBlock>
        {recipe.pathToFile
          ? <a href={`${recipe?.pathToFile}`} target='_blank'>Arquivo de receita anexo</a>
          : <span>Esta receita não possui arquivo anexo</span>}
      </RecipeFileBlock>
    )
  }, [recipe]);

  const diagnostics = React.useMemo(() => {

    if (!recipe.customerDiagnosticInformations || recipe.customerDiagnosticInformations.length === 0) {
      return <Alert variant='warning' style={{ marginTop: '10px' }}>A receita ainda não possui diagnóstico.</Alert>;
    }

    return (
      recipe.customerDiagnosticInformations.map(diagnostic => {
        const distanceTypeDescription = recipeResources.DistanceTypeOptions.find(rto => rto.Type === diagnostic.distanceType)?.Description;
        return (
          <DiagnosticBlock key={`diagnostic-${diagnostic.id}`}>
            <DiagnosticDistanceType>{distanceTypeDescription}</DiagnosticDistanceType>
            <DiagnosticContainerTable>
              <Table>
                <thead>
                  <TableRow>
                    <FixedTableHeadDiagnostic>{strings.Eye}</FixedTableHeadDiagnostic>
                    <TableHeadDiagnostic>{strings.Spherical}</TableHeadDiagnostic>
                    <TableHeadDiagnostic>{strings.Cylindrical}</TableHeadDiagnostic>
                    <TableHeadDiagnostic>{strings.Axis}</TableHeadDiagnostic>
                    <TableHeadDiagnostic>{strings.DNP}</TableHeadDiagnostic>
                    <TableHeadDiagnostic>{strings.Height}</TableHeadDiagnostic>
                  </TableRow>
                </thead>
                <tbody>
                  <TableRow>
                    <FixedTableDataDiagnostic>{strings.Right}</FixedTableDataDiagnostic>
                    <TableDataDiagnostic>{diagnostic.rightSpherical}</TableDataDiagnostic>
                    <TableDataDiagnostic>{diagnostic.rightCylindrical}</TableDataDiagnostic>
                    <TableDataDiagnostic>{diagnostic.rightAxis}</TableDataDiagnostic>
                    <TableDataDiagnostic>{diagnostic.rightNasoPupillaryDistance}</TableDataDiagnostic>
                    <TableDataDiagnostic>{recipe.rightEyeHeight}</TableDataDiagnostic>
                  </TableRow>
                  <TableRow>
                    <FixedTableDataDiagnostic>{strings.Left}</FixedTableDataDiagnostic>
                    <TableDataDiagnostic>{diagnostic.leftSpherical}</TableDataDiagnostic>
                    <TableDataDiagnostic>{diagnostic.leftCylindrical}</TableDataDiagnostic>
                    <TableDataDiagnostic>{diagnostic.leftAxis}</TableDataDiagnostic>
                    <TableDataDiagnostic>{diagnostic.leftNasoPupillaryDistance}</TableDataDiagnostic>
                    <TableDataDiagnostic>{recipe.leftEyeHeight}</TableDataDiagnostic>
                  </TableRow>
                </tbody>
              </Table>
            </DiagnosticContainerTable>
          </DiagnosticBlock>
        )
      })
    );
  }, [recipe]);

  const multifocalInfo = React.useMemo(() => {
    if (!recipe.customerMultifocalInfo) return null;

    return (
      <DiagnosticBlock>
        <DiagnosticDistanceType>Multifocal</DiagnosticDistanceType>
        <DiagnosticContainerTable>
          <Table>
            <thead>
              <TableRow>
                <TableHeadDiagnostic>{strings.Addition}</TableHeadDiagnostic>
                <TableHeadDiagnostic>{strings.Bridge}</TableHeadDiagnostic>
                <TableHeadDiagnostic>{strings.Rim}</TableHeadDiagnostic>
                <TableHeadDiagnostic>{strings.Vertical}</TableHeadDiagnostic>
                <TableHeadDiagnostic>{strings.AbbreviationForLargestDiagonal}</TableHeadDiagnostic>
              </TableRow>
            </thead>
            <tbody>
              <TableRow>
                <TableDataDiagnostic>{recipe.customerMultifocalInfo?.addField}</TableDataDiagnostic>
                <TableDataDiagnostic>{recipe.customerMultifocalInfo?.actualBridge}</TableDataDiagnostic>
                <TableDataDiagnostic>{recipe.customerMultifocalInfo?.actualEyeWidth}</TableDataDiagnostic>
                <TableDataDiagnostic>{recipe.customerMultifocalInfo?.actualEyeHeight}</TableDataDiagnostic>
                <TableDataDiagnostic>{recipe.customerMultifocalInfo?.actualLargestDiagonal}</TableDataDiagnostic>
              </TableRow>
            </tbody>
          </Table>
        </DiagnosticContainerTable>
      </DiagnosticBlock>
    );
  }, [recipe]);

  return (
    <CollapsibleCard
      header={header}
      showExpanded={false}
      marginBottom='5px'
    >
      {recipeFile}
      {diagnostics}
      {multifocalInfo}
    </CollapsibleCard>
  );
}

export { CustomerRecipeCard };